import { useCallback, useEffect, useState } from 'react'
import './CredentialEditModal.css'

/// Generic shape: each existing credential_kind plus a masked preview string.
/// Both tenant-scoped and user-scoped lists are reduced to this shape by the
/// caller (api.ts) before opening the modal.
export interface CredentialEntryView {
  kind: string
  preview: string
}

/// Schema for a single credential_kind in a service form.
interface FieldSpec {
  kind: string
  label: string
  /// When true, render as <input type="password">.
  secret?: boolean
  /// Placeholder text shown when no current value exists.
  placeholder?: string
  /// Help text below the input.
  hint?: string
}

/// Hardcoded per-service field schemas. Add new services here as they go live.
/// Field labels are German because the Cockpit is German-only.
const SERVICE_SCHEMAS: Record<string, { label: string; fields: FieldSpec[] }> = {
  mail: {
    label: 'Mail (IMAP/SMTP)',
    fields: [
      { kind: 'IMAP_HOST', label: 'IMAP Server' },
      { kind: 'IMAP_PORT', label: 'IMAP Port', placeholder: '993' },
      { kind: 'SMTP_HOST', label: 'SMTP Server' },
      { kind: 'SMTP_PORT', label: 'SMTP Port', placeholder: '587' },
      { kind: 'MAIL_USERNAME', label: 'E-Mail Benutzername', placeholder: 'aria@...' },
      { kind: 'MAIL_PASSWORD', label: 'Passwort', secret: true },
      { kind: 'MAIL_FROM_EMAIL', label: 'Absender-Adresse', placeholder: 'aria@...' },
      { kind: 'MAIL_FROM_NAME', label: 'Anzeigename', placeholder: 'Aria' },
    ],
  },
  webdav: {
    label: 'WebDAV (Nextcloud / OpenCloud)',
    fields: [
      {
        kind: 'WEBDAV_CREDENTIALS',
        label: 'WebDAV Verbindungs-String',
        secret: true,
        placeholder: 'https://cloud.example.de|username|password|',
        hint: 'Format: URL|Benutzer|Passwort| (Pipe-separiert, trailing Pipe wichtig)',
      },
    ],
  },
  erpnext: {
    label: 'ERPNext',
    fields: [
      { kind: 'ERPNEXT_URL', label: 'ERPNext URL', placeholder: 'https://erp.example.de' },
      { kind: 'ERPNEXT_API_KEY', label: 'API Key', secret: true },
      { kind: 'ERPNEXT_API_SECRET', label: 'API Secret', secret: true },
    ],
  },
  caldav: {
    label: 'CalDAV Kalender',
    fields: [
      { kind: 'CALDAV_URL', label: 'CalDAV URL' },
      { kind: 'CALDAV_USERNAME', label: 'Benutzername' },
      { kind: 'CALDAV_PASSWORD', label: 'Passwort', secret: true },
    ],
  },
  lexoffice: {
    label: 'lexoffice',
    fields: [
      { kind: 'LEXOFFICE_API_KEY', label: 'API Key', secret: true },
    ],
  },
  bitwarden: {
    label: 'Bitwarden',
    fields: [
      { kind: 'BITWARDEN_URL', label: 'Server URL', placeholder: 'https://vault.example.de' },
      { kind: 'BITWARDEN_CLIENT_ID', label: 'Client ID' },
      { kind: 'BITWARDEN_CLIENT_SECRET', label: 'Client Secret', secret: true },
    ],
  },
  wordpress: {
    label: 'WordPress',
    fields: [
      { kind: 'WORDPRESS_URL', label: 'WordPress URL', placeholder: 'https://example.de' },
      { kind: 'WORDPRESS_USERNAME', label: 'Benutzername' },
      { kind: 'WORDPRESS_APP_PASSWORD', label: 'Application Password', secret: true },
    ],
  },
  opencloud: {
    label: 'OpenCloud',
    fields: [
      { kind: 'OPENCLOUD_URL', label: 'OpenCloud URL' },
      { kind: 'OPENCLOUD_USERNAME', label: 'Benutzername' },
      { kind: 'OPENCLOUD_PASSWORD', label: 'Passwort', secret: true },
    ],
  },
  keycloak: {
    label: 'Keycloak',
    fields: [
      { kind: 'KEYCLOAK_URL', label: 'Keycloak URL' },
      { kind: 'KEYCLOAK_CLIENT_ID', label: 'Client ID' },
      { kind: 'KEYCLOAK_CLIENT_SECRET', label: 'Client Secret', secret: true },
    ],
  },
  clockin: {
    label: 'Clockin Zeiterfassung',
    fields: [
      { kind: 'CLOCKIN_URL', label: 'Clockin URL' },
      { kind: 'CLOCKIN_API_KEY', label: 'API Key', secret: true },
    ],
  },
}

/// Fallback schema generator for services without a hardcoded form.
/// Uses the existing credential_kinds in DB to build the field list.
function fallbackSchema(
  service: string,
  existing: CredentialEntryView[],
): { label: string; fields: FieldSpec[] } {
  const fields: FieldSpec[] = existing.map((e) => ({
    kind: e.kind,
    label: e.kind,
    secret: true,
  }))
  return {
    label: service,
    fields,
  }
}

export interface CredentialEditModalProps {
  service: string
  /// Existing entries (kind + masked preview). Empty list = no credentials yet.
  existing: CredentialEntryView[]
  /// Modal title and subtitle help text. Differs between tenant + user scope.
  title: string
  subtitle?: string
  /// Hint shown above the fields. e.g. "Felder leer = unverändert".
  hint?: string
  /// Caller-supplied save handler. Receives `{kind: plaintext_secret}` for
  /// every non-empty field. Throws on failure.
  onSave: (values: Record<string, string>) => Promise<{ kindsUpdated: string[] }>
  /// Caller-supplied delete handler. Removes ALL credentials for this service.
  onDelete: () => Promise<void>
  onClose: () => void
  /// Called after a successful save OR delete, so the parent can refresh.
  onSaved: () => void
}

export default function CredentialEditModal({
  service,
  existing,
  title,
  subtitle,
  hint,
  onSave,
  onDelete,
  onClose,
  onSaved,
}: CredentialEditModalProps) {
  const schema = SERVICE_SCHEMAS[service] ?? fallbackSchema(service, existing)

  const existingMap = new Map(existing.map((e) => [e.kind, e]))

  const [values, setValues] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    const payload: Record<string, string> = {}
    for (const [kind, val] of Object.entries(values)) {
      if (val.trim() === '') continue
      payload[kind] = val
    }
    if (Object.keys(payload).length === 0) {
      setError('Mindestens ein Feld muss ausgefüllt sein.')
      setSaving(false)
      return
    }
    try {
      const result = await onSave(payload)
      setSuccess(`${result.kindsUpdated.length} Feld(er) gespeichert.`)
      setValues({})
      onSaved()
    } catch (err) {
      console.error('[CredentialEditModal] save failed:', err)
      setError(
        (err && typeof err === 'object' && 'error' in err
          ? String((err as { error: string }).error)
          : 'Speichern fehlgeschlagen.'),
      )
    } finally {
      setSaving(false)
    }
  }, [values, onSave, onSaved])

  const handleDelete = useCallback(async () => {
    if (!confirm(`Wirklich ALLE Credentials für "${service}" löschen? Aria verliert den Zugriff sofort.`)) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onDelete()
      onSaved()
      onClose()
    } catch (err) {
      console.error('[CredentialEditModal] delete failed:', err)
      setError(
        (err && typeof err === 'object' && 'error' in err
          ? String((err as { error: string }).error)
          : 'Löschen fehlgeschlagen.'),
      )
      setSaving(false)
    }
  }, [service, onDelete, onSaved, onClose])

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, saving])

  return (
    <div className="cred-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="cred-modal" onClick={(e) => e.stopPropagation()}>
        <header className="cred-modal-header">
          <div>
            <h2>{title}</h2>
            <div className="cred-modal-subtitle">{subtitle ?? schema.label}</div>
          </div>
          <button className="cred-modal-close" onClick={onClose} disabled={saving} aria-label="Schließen">
            ×
          </button>
        </header>

        <p className="cred-modal-note">
          {hint ?? (
            <>
              Felder die leer bleiben werden <strong>nicht</strong> verändert.
              Felder mit aktueller Belegung zeigen die letzten 4 Zeichen als Hinweis.
            </>
          )}
        </p>

        <div className="cred-modal-fields">
          {schema.fields.length === 0 ? (
            <p className="cred-modal-empty">
              Keine Felder definiert. Diesen Service kann derzeit nur per API gepflegt werden.
            </p>
          ) : (
            schema.fields.map((field) => {
              const current = existingMap.get(field.kind)
              const placeholder = current
                ? `Aktuell: ${current.preview}`
                : field.placeholder ?? '(neu)'
              return (
                <div className="cred-modal-field" key={field.kind}>
                  <label htmlFor={`cred-${field.kind}`}>{field.label}</label>
                  <input
                    id={`cred-${field.kind}`}
                    type={field.secret ? 'password' : 'text'}
                    value={values[field.kind] ?? ''}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field.kind]: e.target.value }))
                    }
                    placeholder={placeholder}
                    disabled={saving}
                    autoComplete="new-password"
                  />
                  {field.hint && <div className="cred-modal-hint">{field.hint}</div>}
                </div>
              )
            })
          )}
        </div>

        {error && <div className="cred-modal-error">{error}</div>}
        {success && <div className="cred-modal-success">{success}</div>}

        <footer className="cred-modal-footer">
          <button
            className="cred-modal-btn cred-modal-btn--danger"
            onClick={handleDelete}
            disabled={saving || existing.length === 0}
            title={existing.length === 0 ? 'Nichts zu löschen' : 'Alle Credentials und Grant entfernen'}
          >
            Alles löschen
          </button>
          <div className="cred-modal-footer-right">
            <button className="cred-modal-btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button
              className="cred-modal-btn cred-modal-btn--primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
